import React, { useEffect, useRef, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { Logger } from "@ng-mw/shared-react-components"
import MenyLocalStorage from "@app/MenyLocalStorage"
import { hideFeedbackPopup, showFeedbackSuccess } from "@app/store/app/appActions"
import { RootState } from "@app/types"
import { triggerEventNow } from "@modules/shared/MessageAfterEvent"
import { normalizeFeedbackPlatform, submitFeedback } from "../api/FeedbackApi"

// Components
import FeedbackForm, { FeedbackFormActions } from "../FeedbackForm"
import FeedbackDrawer from "../FeedbackDrawer"

const FeedbackPopup = () => {
    const dispatch = useDispatch<any>()
    const { visible: isOpen, data } = useSelector((state: RootState) => state.app.feedbackPopup)
    const appEnvConfig = useSelector((state: RootState) => state.appEnvConfig)
    const rhash = useSelector((state: RootState) => state.user.automaticTrackingData?.rhash)
    const id = data?.id
    const [feedback, setFeedback] = useState("")
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [submitError, setSubmitError] = useState<string>()
    const isSubmissionInProgress = useRef(false)

    useEffect(() => {
        if (isOpen) {
            setFeedback("")
            setSubmitError(undefined)
            isSubmissionInProgress.current = false
        }
    }, [isOpen, data?.id])

    const hideDrawer = () => {
        triggerEventNow(`${MenyLocalStorage.UserInteractedWithFeedback}${id}`)
        dispatch(hideFeedbackPopup())
    }

    const sendFeedback = async (comment?: string, shouldShowError = true) => {
        if (!data || isSubmissionInProgress.current) return false

        isSubmissionInProgress.current = true
        setIsSubmitting(true)
        setSubmitError(undefined)

        try {
            await submitFeedback({
                appVersion: appEnvConfig.version,
                comment,
                osVersion: appEnvConfig.osVersion,
                page: data.page,
                platform: normalizeFeedbackPlatform(appEnvConfig.os),
                question: data.feedbackQuestion,
                rating: data.isThumbsUp ? "1" : "0",
                rhash,
            })
            return true
        } catch (error) {
            Logger.error("modules/shared/FeedbackPopup/submitFeedback()", error)
            if (shouldShowError) {
                setSubmitError("Vi klarte ikke å sende tilbakemeldingen. Prøv igjen.")
            }
            return false
        } finally {
            isSubmissionInProgress.current = false
            setIsSubmitting(false)
        }
    }

    const handleSubmit = async () => {
        const wasSubmitted = await sendFeedback(feedback.trim() || undefined)

        if (wasSubmitted) {
            dispatch(showFeedbackSuccess(data?.id || ""))
            hideDrawer()
        }
    }

    const handleDismiss = () => {
        if (!data || isSubmissionInProgress.current) return

        hideDrawer()
        void sendFeedback(undefined, false)
    }

    const handleFeedbackChange = (value: string) => {
        setFeedback(value)
        setSubmitError(undefined)
    }

    return (
        <FeedbackDrawer
            footer={
                <FeedbackFormActions
                    isSubmitting={isSubmitting}
                    onClose={handleDismiss}
                    onSubmit={handleSubmit}
                />
            }
            isOpen={isOpen}
            onClose={handleDismiss}
        >
            <FeedbackForm
                feedback={feedback}
                onFeedbackChange={handleFeedbackChange}
                question={data?.question || ""}
                submitError={submitError}
            />
        </FeedbackDrawer>
    )
}

export default FeedbackPopup
